import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/lib/constants";
import { motion } from "framer-motion";
import { ScrollText, ShieldCheck, Scale, Globe, CheckCircle2, Circle, ArrowRight, Gavel } from "lucide-react";

const RATIFIED = [
  {
    name: "International Convention on the Elimination of All Forms of Racial Discrimination (ICERD)",
    year: "Ratified by the U.S. Senate — 1994",
    note: "Deposited October 21, 1994. The most widely ratified human rights treaty in history (182 States). Article 2 binds all U.S. authorities — national, state, and local — to eliminate racial discrimination. Ratified with a 'non-self-executing' understanding and a federalism understanding (which the U.S. itself affirms 'is not a reservation' and cannot excuse non-compliance).",
  },
  {
    name: "International Covenant on Civil and Political Rights (ICCPR)",
    year: "Ratified by the U.S. Senate — 1992",
    note: "Article 1 recognizes that 'all peoples have the right of self-determination' — to freely determine their political status and pursue their economic, social, and cultural development. Article 27 protects the rights of ethnic, religious, and linguistic minorities to enjoy their own culture in community with other members of their group.",
  },
  {
    name: "Convention Against Torture (CAT) & Genocide Convention",
    year: "Ratified by the U.S. Senate — 1994 / 1988",
    note: "Part of the body of human rights treaties the United States has accepted as binding federal law under the Supremacy Clause, reinforcing the principle that ratified treaties carry domestic legal force.",
  },
];

const BINDING_OTHERWISE = [
  {
    name: "American Declaration of the Rights and Duties of Man (1948)",
    note: "Binds the United States as a member of the Organization of American States (OAS) — independent of treaty ratification. Recognizes equality before the law (Art. II) and the right to participate in cultural life (Art. XIII).",
  },
];

const CITED_AUTHORITY = [
  { name: "American Declaration on the Rights of Indigenous Peoples (ADRIP, 2016)", note: "Article I(2) expressly extends its protections to 'peoples of African descent who have been displaced and who maintain cultural characteristics linked to their traditional territory.' Adopted by consensus of all 35 OAS states." },
  { name: "UN Declaration on the Rights of Indigenous Peoples (UNDRIP, 2007)", note: "Article 3 affirms the right of self-determination; endorsed by the United States in 2010." },
  { name: "ILO Convention No. 169 (1989)", note: "Article 1(2): self-identification as tribal 'shall be regarded as a fundamental criterion.' Defines tribal peoples by distinct social, cultural, and economic conditions." },
  { name: "CERD General Recommendation No. 34 (2011)", note: "The treaty body's authoritative interpretation of ICERD as applied to people of African descent — a distinct group deserving special measures and recognition of collective rights." },
  { name: "Durban Declaration & Programme of Action (2001)", note: "163 States recognized people of African descent as a specific group and declared the Trans-Atlantic Slave Trade a crime against humanity." },
];

const DOCTRINES = [
  { t: "Tribal Peoples Doctrine", d: "Afrodescendant communities organized around shared cultural identity, distinct institutions, and a relationship to territory qualify as tribal peoples — even when displaced. (Saramaka v. Suriname, IACtHR 2007)" },
  { t: "Self-Identification Doctrine", d: "A community's own self-identification is the decisive criterion of tribal status. No State may override it on the basis of blood quantum or assimilation. (ILO 169 Art. 1(2); CERD GR 34)" },
  { t: "Non-Extinction Doctrine", d: "Forced displacement and the destruction of language, name, and family by the Slave Trade do not extinguish a people's legal status or rights. (UNDRIP Arts. 8, 10; Moiwana v. Suriname)" },
  { t: "Self-Determination", d: "All peoples may freely determine their political status and pursue cultural and economic development. (ICCPR Art. 1 — ratified by the U.S.; UNDRIP Art. 3)" },
];

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function LegalStanding() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-24 grid lg:grid-cols-3 gap-12 items-center">
        <div className="lg:col-span-2">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">Legal Foundation</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[0.98] text-[#F5F3EF]">
            Our Right to Exist<br />as a People
          </h1>
          <p className="mt-7 text-lg text-[#A8A29E] leading-relaxed max-w-2xl">
            Black Americans are an <span className="text-[#F5F3EF] font-medium">Afrodescendant people</span> — one of
            many tribal communities across the Americas displaced by the Trans-Atlantic Slave Trade and colonialism. Our
            right to associate, self-identify, and form our own governing and political body is grounded in the
            U.S. Constitution and in international covenants the United States has accepted as binding law.
          </p>
        </div>
        <div className="hidden lg:flex justify-center">
          <img src={ASSETS.seal} alt="National Seal" className="h-56 w-56 object-contain" />
        </div>
      </section>

      {/* Who are Afrodescendants */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <ScrollText className="w-8 h-8 text-[#D4AF37] mb-4" strokeWidth={1.5} />
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight">The Legal Definition of an Afrodescendant People</h2>
            </div>
            <div className="lg:col-span-2 space-y-5 text-[#C9C5BD] leading-relaxed">
              <p>
                Under international and inter-American law, <span className="text-[#F5F3EF] font-medium">Afrodescendant peoples</span> —
                the descendants of those enslaved and forcibly transported through the Trans-Atlantic Slave Trade — are
                recognized as a <span className="text-[#D4AF37] font-medium">rights-bearing tribal collective</span>. They are
                entitled to collective identity, self-determination, cultural preservation, and reparative justice.
              </p>
              <p>
                The Inter-American Court of Human Rights first held in <span className="italic">Saramaka People v. Suriname</span> (2007)
                that an Afrodescendant people constitutes a <span className="text-[#F5F3EF] font-medium">"tribal people"</span> under
                international law. The Court later clarified that <span className="text-[#F5F3EF] font-medium">self-identification</span> is
                the primary criterion of that status — it cannot be defeated by displacement, assimilation, or the absence of formal records.
              </p>
              <div className="doc-scroll border border-[#D4AF37]/30 p-6 font-serif-display text-lg text-[#EDE9E1] leading-relaxed">
                "The peoples of African descent who have been displaced and who maintain cultural characteristics linked to
                their traditional territory are considered to fall within the framework of this Declaration."
                <span className="block mt-3 text-sm text-[#A8A29E] font-sans">— American Declaration on the Rights of Indigenous Peoples, Article I(2), OAS, 2016</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supremacy Clause */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="grid lg:grid-cols-3 gap-12">
          <div>
            <Gavel className="w-8 h-8 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight">The Supremacy Clause & the Right to Self-Govern</h2>
          </div>
          <div className="lg:col-span-2 space-y-5 text-[#C9C5BD] leading-relaxed">
            <p>
              <span className="text-[#F5F3EF] font-medium">Article VI of the U.S. Constitution</span> — the Supremacy Clause —
              provides that treaties made under the authority of the United States are part of <span className="text-[#D4AF37] font-medium">"the supreme Law of the Land,"</span> binding
              on every judge and on all federal, state, and local authorities.
            </p>
            <div className="doc-scroll border border-[#D4AF37]/30 p-6 font-serif-display text-lg text-[#EDE9E1] leading-relaxed">
              "This Constitution, and the Laws of the United States… and all Treaties made, or which shall be made, under
              the Authority of the United States, shall be the supreme Law of the Land."
              <span className="block mt-3 text-sm text-[#A8A29E] font-sans">— U.S. Constitution, Article VI, Clause 2</span>
            </div>
            <p>
              Two pillars secure our right to organize as a people and form an associated government and political body:
            </p>
            <ul className="space-y-3">
              <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-[#5DBB7A] shrink-0 mt-0.5" /><span><span className="text-[#F5F3EF] font-medium">The First Amendment</span> guarantees the rights of assembly, free association, free exercise, and to petition government — the constitutional basis for voluntarily uniting as one people and establishing our own institutions.</span></li>
              <li className="flex gap-3"><Globe className="w-5 h-5 text-[#5DBB7A] shrink-0 mt-0.5" /><span><span className="text-[#F5F3EF] font-medium">Ratified covenants</span> (below) recognize the right of <span className="text-[#D4AF37]">self-determination</span> and the collective rights of peoples of African descent — and, through Article VI, carry the force of federal law.</span></li>
            </ul>
            <p className="text-sm text-[#A8A29E]">
              Membership in this Nation complements — and does not by itself alter or extinguish — any citizenship or
              legal status a member presently holds under the laws of the United States or any State.
            </p>
          </div>
        </div>
      </section>

      {/* Ratified covenants */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">Binding Federal Law</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-4">Covenants Ratified by the U.S. Senate</h2>
          <p className="text-[#A8A29E] max-w-3xl mb-12">These instruments were ratified by the United States Senate and therefore stand as the supreme law of the land under Article VI. They are the strongest legal anchors of our claims.</p>

          <div className="grid md:grid-cols-3 gap-6">
            {RATIFIED.map((c) => (
              <motion.div key={c.name} {...FADE} className="bg-[#141414] border border-[#5DBB7A]/40 p-7" data-testid="ratified-covenant-card">
                <div className="inline-flex items-center gap-2 text-[#5DBB7A] text-xs font-semibold uppercase tracking-wider mb-4">
                  <CheckCircle2 className="w-4 h-4" /> Senate-Ratified
                </div>
                <h3 className="font-serif-display text-2xl text-[#F5F3EF] leading-snug mb-2">{c.name}</h3>
                <div className="text-[#D4AF37] text-sm font-medium mb-3">{c.year}</div>
                <p className="text-sm text-[#A8A29E] leading-relaxed">{c.note}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {BINDING_OTHERWISE.map((c) => (
              <div key={c.name} className="bg-[#141414] border border-[#D4AF37]/30 p-7">
                <div className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-wider mb-4">
                  <ShieldCheck className="w-4 h-4" /> Binding on the U.S. (OAS Membership)
                </div>
                <h3 className="font-serif-display text-2xl text-[#F5F3EF] leading-snug mb-2">{c.name}</h3>
                <p className="text-sm text-[#A8A29E] leading-relaxed">{c.note}</p>
              </div>
            ))}
            <div className="bg-[#141414] border border-[#F5F3EF]/15 p-7">
              <div className="inline-flex items-center gap-2 text-[#A8A29E] text-xs font-semibold uppercase tracking-wider mb-4">
                <Circle className="w-4 h-4" /> Persuasive International Authority
              </div>
              <h3 className="font-serif-display text-2xl text-[#F5F3EF] leading-snug mb-3">Declarations & Conventions Cited in Support</h3>
              <ul className="space-y-3">
                {CITED_AUTHORITY.map((c) => (
                  <li key={c.name} className="text-sm">
                    <span className="text-[#F5F3EF] font-medium">{c.name}.</span>{" "}
                    <span className="text-[#A8A29E]">{c.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Doctrines */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">Governing Principles</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-12">Legal Doctrines That Affirm Our Standing</h2>
        <div className="grid sm:grid-cols-2 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15">
          {DOCTRINES.map((d) => (
            <div key={d.t} className="bg-[#0A0A0A] p-8">
              <div className="flex items-center gap-2 mb-3">
                <Scale className="w-5 h-5 text-[#D4AF37]" />
                <h3 className="font-serif-display text-2xl text-[#F5F3EF]">{d.t}</h3>
              </div>
              <p className="text-sm text-[#A8A29E] leading-relaxed">{d.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + Disclaimer */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-20">
        <div className="bg-[#141414] border border-[#D4AF37]/30 p-10 sm:p-14 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight max-w-2xl mx-auto">Take your place in the legal record of our people.</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/register" data-testid="legal-register-btn">
              <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">Become a Member <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </Link>
            <Link to="/cerd-obligations" data-testid="legal-to-cerd-btn">
              <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">CERD Obligations</Button>
            </Link>
          </div>
        </div>
        <p className="text-xs text-[#A8A29E]/70 leading-relaxed mt-8 max-w-4xl mx-auto text-center">
          This page is provided for educational and informational purposes to explain the legal framework on which the
          Nation is organized. It is not legal advice and does not create an attorney-client relationship. Citations
          summarize public instruments and decisions; readers should consult the primary sources and qualified counsel.
        </p>
      </section>

      <Footer />
    </div>
  );
}
